import * as React from 'react';
import Box from '@mui/material/Box';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Typography from '@mui/material/Typography';
import { Navigate, useSearchParams } from 'react-router-dom';

import EditeurParametresAllures from '../Allures/EditeurParametresAllures.tsx';
import AjustementRecupEditeur from './AjustementRecupEditeur.tsx';
import ApparenceEditeur from './ApparenceEditeur.tsx';
import CoursePreparee from './CoursePreparee.tsx';
import FuseauHoraireEditeur from './FuseauHoraireEditeur.tsx';
import ModeleSeanceEditeur from './ModeleSeanceEditeur.tsx';
import IntervalsIcuEditeur from './IntervalsIcuEditeur.tsx';
import OffreEditeur from './OffreEditeur.tsx';
import ZoneDangerEditeur from './ZoneDangerEditeur.tsx';
import { estAdministrateurSeul, estCoach, useMoi } from '../Auth/moi.ts';

// Onglets lateraux de la page (l'onglet courant est dans l'adresse,
// ex : /preferences?onglet=allures, pour pouvoir y renvoyer directement).
// coach : onglet reserve au coach ; athlete : onglet reserve a l'athlete.
// Les athletes du coach (invitations, modeles, fuseau de chacun) sont sur
// la page Athletes ; le club (coachs, bibliotheque partagee) sur la page Club.
const ONGLETS = [
  // Zones d'allure : domaine du coach (l'athlete utilise les siennes sans les voir ici).
  { cle: 'allures', libelle: 'Zones d\'allure', coach: true },
  // Course preparee : fixee par le coach (page Athletes), consultee par l'athlete.
  { cle: 'preparation', libelle: 'Ma préparation', coach: false, athlete: true },
  // Ajustement des recuperations : connu du coach seulement (l'athlete voit
  // ses seances ajustees, sans le reglage).
  { cle: 'recuperations', libelle: 'Paramètres des récupérations', coach: true },
  { cle: 'modeles', libelle: 'Paramètres des modèles', coach: true },
  // Jour courant du calendrier (jours passes non modifiables).
  { cle: 'fuseau', libelle: 'Mon fuseau horaire', coach: false },
  // Modeles : choisis par le coach (pour lui ou un athlete), consultes par l'athlete.
  // Offre choisie a la premiere connexion (et coach de l'athlete).
  // Integration intervals.icu : activites de l'entrainement (pas pour l'administrateur qui n'entraine pas).
  { cle: 'intervals', libelle: 'Mes clés d\'intégration', coach: false, entrainement: true },
  { cle: 'offre', libelle: 'Mon offre produit', coach: false },
  // Mode clair / sombre (automatique par defaut), propre a l'appareil.
  { cle: 'apparence', libelle: 'Apparence', coach: false },
  // Suppression du compte (athlete sans coach, coach sans athlete).
  { cle: 'danger', libelle: 'Zone de danger', coach: false },
] as const;
type CleOnglet = (typeof ONGLETS)[number]['cle'];

// Page des preferences de l'utilisateur connecte, accessible depuis le
// menu, en onglets : les parametres d'allures (table de zones utilisee
// par les pages Seances et Modeles), les recuperations, les
// modeles de seance, etc.
export default function Preferences({ user }: { user: any }) {
  const [params, setParams] = useSearchParams();
  const demande = params.get('onglet');
  const moi = useMoi();
  const coach = estCoach(moi);
  // Administrateur de club qui n'entraine pas : ni onglets du coach, ni ceux de l'athlete.
  const sansEntrainement = estAdministrateurSeul(moi);
  const onglets = ONGLETS.filter((o) => (coach || sansEntrainement ? !('athlete' in o && o.athlete) : true)
    && (coach || !o.coach)
    && !(sansEntrainement && 'entrainement' in o && o.entrainement));
  // Sans onglet demande (ou onglet reserve au coach) : le premier.
  const onglet: CleOnglet = onglets.find((o) => o.cle === demande)?.cle ?? onglets[0].cle;

  // Anciennes adresses : athletes du coach et modeles d'un athlete (page Athletes).
  if (coach && demande === 'athletes') return <Navigate to="/athletes" replace />;
  const ancienAthlete = coach && demande === 'modeles' ? params.get('athlete') : null;
  if (ancienAthlete) {
    const lien = moi?.athletes.find((a) => a.ssoUserId === ancienAthlete);
    return <Navigate to={lien ? `/athletes/${encodeURIComponent(lien.id)}?onglet=modeles` : '/athletes'} replace />;
  }

  return (
    <Box component="main" sx={{ p: 12, pt: 10 }}>

      {user?.name && (
        <Typography sx={{ mb: 2 }} color="text.secondary">
          {user.name}{user.email ? ` — ${user.email}` : ''}
        </Typography>
      )}

      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3, mt: 4 }}>
        <Tabs
          orientation="vertical"
          value={onglet}
          onChange={(_e, valeur: CleOnglet) => setParams({ onglet: valeur }, { replace: true })}
          TabIndicatorProps={{ sx: onglet === 'danger' ? { bgcolor: 'error.main' } : undefined }}
          sx={{
            borderRight: { md: 1 },
            borderColor: 'divider',
            flexShrink: 0,
            minWidth: 220,
            '& .MuiTab-root': { alignItems: 'flex-start', textAlign: 'left', textTransform: 'none', fontSize: '1rem' },
          }}
        >
          {onglets.map((o) => (
            <Tab
              key={o.cle}
              value={o.cle}
              label={o.libelle}
              // Zone de danger en rouge, selectionnee ou non.
              sx={o.cle === 'danger' ? { color: 'error.main', '&.Mui-selected': { color: 'error.main' } } : undefined}
            />
          ))}
        </Tabs>

        {/* Contenu de l'onglet (sans la marge haute du premier bloc) */}
        <Box sx={{ flex: 1, minWidth: 0, '& > :first-of-type': { mt: 0 }, '& > :first-of-type > h2:first-of-type': { mt: 0 } }}>
          {onglet === 'recuperations' && coach && <AjustementRecupEditeur />}
          {onglet === 'modeles' && coach && <ModeleSeanceEditeur />}
          {onglet === 'preparation' && !coach && <CoursePreparee />}
          {onglet === 'fuseau' && <FuseauHoraireEditeur />}
          {onglet === 'allures' && coach && <EditeurParametresAllures />}
          {onglet === 'intervals' && <IntervalsIcuEditeur />}
          {onglet === 'offre' && <OffreEditeur />}
          {onglet === 'apparence' && <ApparenceEditeur />}
          {onglet === 'danger' && <ZoneDangerEditeur />}
        </Box>
      </Box>
    </Box>
  );
}
